import React, { useState, useEffect } from 'react';
import Login from './features/auth/Login';
import MainLayout from './components/Layout/MainLayout';
import DashboardHome from './features/dashboard/DashboardHome';
import Profile from './features/auth/Profile';
import UserManagement from './features/admin/UserManagement';
import Sidebar from './components/Layout/Sidebar';
import Topbar from './components/Layout/Topbar';
import RoleConfiguration from './features/admin/RoleConfiguration';
import AuditLogs from './features/admin/AuditLogs';
import RiskRegister from './features/admin/RiskRegister';
import SHERegister from './features/admin/SHERegister';
import RiskAssessment from './features/admin/RiskAssessment';
import ControlsTreatment from './features/admin/ControlsTreatment';
import AddSHEActivity from './features/admin/AddSHEActivity';
import AddRisk from './features/admin/AddRisk';
import SHEView from './features/admin/SHEView';
import RiskView from './features/admin/RiskView';
import UserPermissions from './features/admin/UserPermissions';
import MonteCarloAnalysis from './features/dashboard/MonteCarloAnalysis';
import api from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState(localStorage.getItem('activeTab') || 'dashboard');
  const [selectedRole, setSelectedRole] = useState(JSON.parse(localStorage.getItem('selectedRole')) || null);
  const [viewData, setViewData] = useState(null);
  const [redirectionMessage, setRedirectionMessage] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('auth_token');
    
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
      // Verify session with backend
      api.get('/user').catch(() => {
        handleLogout();
      });
    }

    const handleUnauthorized = () => {
      handleLogout();
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => window.removeEventListener('unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (selectedRole) localStorage.setItem('selectedRole', JSON.stringify(selectedRole));
    else localStorage.removeItem('selectedRole');
  }, [selectedRole]);

  const handleLogin = (userData) => {
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    setActiveTab('dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('activeTab');
    localStorage.removeItem('selectedRole');
    setActiveTab('dashboard');
    setSelectedRole(null);
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const renderContent = () => {
    const defaultPlaceholder = (title, desc) => (
      <div className="card glass animate-fade-in">
        <h2 style={{ marginBottom: '16px' }}>{title}</h2>
        <p style={{ color: 'var(--text-muted)' }}>{desc}</p>
        <div style={{ marginTop: '32px', padding: '40px', border: '2px dashed var(--border-color)', borderRadius: '12px', textAlign: 'center' }}>
          <p>Module integration in progress...</p>
        </div>
      </div>
    );

    switch(activeTab) {
      case 'dashboard': return <DashboardHome />;
      case 'profile': return <Profile />;
      case 'users': return <UserManagement 
        onViewPermissions={(user) => {
          setViewData(user);
          setActiveTab('user-permissions');
        }}
        onRoleSelect={(role) => {
          setSelectedRole(role);
          setActiveTab('role-config');
        }} 
      />;
      case 'user-permissions': return <UserPermissions 
        user={viewData} 
        onBack={() => {
          setActiveTab('users');
          setViewData(null);
        }} 
      />;
      case 'role-config': return selectedRole ? (
        <RoleConfiguration 
          role={selectedRole} 
          onBack={() => {
            setActiveTab('users');
            setSelectedRole(null);
          }} 
        />
      ) : <UserManagement onRoleSelect={(role) => {
        setSelectedRole(role);
        setActiveTab('role-config');
      }} />;
      case 'she': return <SHERegister 
        successMessage={redirectionMessage}
        onClearMessage={() => setRedirectionMessage(null)}
        onAdd={() => {
            setViewData(null);
            setRedirectionMessage(null);
            setActiveTab('she-add');
        }} 
        onView={(data) => {
            setViewData(data);
            setRedirectionMessage(null);
            setActiveTab('she-view');
        }}
        onEdit={(data) => {
            setViewData(data);
            setRedirectionMessage(null);
            setActiveTab('she-edit');
        }}
      />;
      case 'she-add': return <AddSHEActivity 
        onBack={(msg) => {
            if (msg && typeof msg === 'string') setRedirectionMessage(msg);
            setActiveTab('she');
        }} 
      />;
      case 'she-edit': return <AddSHEActivity 
        initialData={viewData} 
        onBack={(msg) => {
            if (msg && typeof msg === 'string') setRedirectionMessage(msg);
            setActiveTab('she');
        }} 
      />;
      case 'she-view': return <SHEView 
        data={viewData} 
        user={user}
        onBack={() => setActiveTab('she')} 
        onEdit={() => setActiveTab('she-edit')}
      />;
      case 'risks': return <RiskRegister 
        user={user}
        selectedRole={selectedRole}
        onAdd={() => setActiveTab('risks-add')} 
        onView={(data) => {
            setViewData(data);
            setActiveTab('risks-view');
        }}
      />;
      case 'risks-add': return <AddRisk onBack={() => setActiveTab('risks')} />;
      case 'risks-view': return <RiskView data={viewData} onBack={() => setActiveTab('risks')} />;
      case 'assessment': return <RiskAssessment />;
      case 'controls': return <ControlsTreatment />;
      case 'losses': return defaultPlaceholder('Loss Events', 'Capture and analyze realized losses and incident financial impacts.');
      case 'montecarlo': return <MonteCarloAnalysis />;
      case 'bcm': return defaultPlaceholder('Business Continuity', 'Ensure operational resilience and continuity planning.');
      case 'reports': return defaultPlaceholder('Analysis & Reports', 'Comprehensive visual analytics, trend reports, and institutional risk summaries.');
      case 'Sessions & Logs': return <AuditLogs />;
      default: return <DashboardHome />;
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
        onLogout={handleLogout}
        user={user}
        isMobileOpen={isMobileOpen}
        setMobileOpen={setIsMobileOpen}
      />
      
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)}></div>
      )}

      <main className="main-content">
        <Topbar 
          user={user} 
          onLogout={handleLogout} 
          activeTab={activeTab} 
          onMenuClick={() => setIsMobileOpen(true)}
        />
        <div className="content-area" key={activeTab} style={{ 
          width: '100%', 
          overflowX: 'hidden', 
          maxWidth: '100vw'
        }}>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default App;
